/**
 * ตัวแยก payload: สตริงดิบจาก QR → ข้อมูลชั้น A
 * ฟังก์ชันบริสุทธิ์ ไม่มี I/O ไม่มี dependency ของ Node
 *
 * ### ข้อบังคับที่โค้ดนี้ต้องรักษาไว้เสมอ
 * - `trans_ref` คือ substring ที่คัดมาจาก payload **ตรงตัว** ห้าม trim ห้าม upper
 *   ห้ามตัดขีด ห้ามตัดศูนย์นำหน้า เพราะ `receipts.ref_hash` คำนวณจาก
 *   `upper(trim(trans_ref))` ที่ฝั่งฐานข้อมูล และเป็น unique index กันสลิปซ้ำ (กฎเหล็กข้อ 6)
 *   ถ้าเรา normalize ซ้ำที่นี่ สลิปคนละใบอาจกลายเป็น hash เดียวกัน หรือใบเดิมกลายเป็นคนละ hash
 * - ห้ามคืนยอดเงิน / ผู้โอน / ผู้รับ / เวลาโอน แม้ payload จะมี (กฎเหล็กข้อ 8 และ 5)
 *   tag 54 (amount) ของ EMVCo ถูกใส่ใน DENY_TAGS โดยเจตนา
 */

import { parseTlvDeep, flattenTlv, type TlvNode } from "./tlv.js";
import { isBotBankCode, HOST_TO_BANK, KNOWN_AIDS } from "./banks.js";
import type { PayloadParseResult } from "./types.js";

/* ------------------------------------------------------------------ */
/* EMVCo TLV                                                           */
/* ------------------------------------------------------------------ */

/**
 * tag ที่ห้ามหยิบมาเป็น trans_ref เด็ดขาด
 * - 01 point of initiation
 * - 52 MCC, 53 currency, 58 country, 59 merchant name, 60 city, 62 additional data template
 * - **54 amount** และ 55/56/57 tip/fee — ข้อมูลชั้น B ห้ามหลุดออกจากแพ็กเกจนี้
 * - 63 CRC
 *
 * **`00` ไม่อยู่ในรายการนี้แล้ว** — ในรูปแบบ QR ตรวจสอบสลิป tag `00` เป็น template ที่เนื้อ
 * ทั้งหมดอยู่ข้างใน การกันไว้ทำให้เลขอ้างอิงถูกตัดออกจากผู้เข้าชิงตั้งแต่ต้น (FORMATS.md หัวข้อ 4)
 * ค่า `00` ของ EMVCo payment QR เป็นเลขเวอร์ชันสั้น (`"01"`) ซึ่ง `looksLikeRef()` คัดออกอยู่แล้ว
 */
const DENY_TAGS = new Set(["01", "52", "53", "54", "55", "56", "57", "58", "59", "60", "63"]);

/** ค่าที่ไม่ใช่เลขอ้างอิงแน่ๆ */
const DENY_VALUES = new Set(["TH", "th", "764", "THB", "00", "01", "02", "11", "12"]);

const looksLikeRef = (v: string): boolean =>
  /^[0-9A-Za-z][0-9A-Za-z-]{9,49}$/.test(v) && /[0-9]/.test(v) && !/^(.)\1+$/.test(v);

/** CRC-16/CCITT-FALSE ตามภาคผนวก EMVCo — ใช้ยืนยันว่า payload เป็น EMVCo จริง */
export function crc16ccitt(input: string): string {
  let crc = 0xffff;
  for (let i = 0; i < input.length; i++) {
    crc ^= input.charCodeAt(i) << 8;
    for (let b = 0; b < 8; b++) {
      crc = crc & 0x8000 ? ((crc << 1) ^ 0x1021) & 0xffff : (crc << 1) & 0xffff;
    }
  }
  return crc.toString(16).toUpperCase().padStart(4, "0");
}

/** true เมื่อ payload มี tag 63 และค่า CRC ตรง — สัญญาณยืนยัน EMVCo ที่ชัดที่สุด */
export function hasValidEmvcoCrc(payload: string): boolean {
  const idx = payload.lastIndexOf("6304");
  if (idx < 0 || idx + 8 !== payload.replace(/\s+$/, "").length) return false;
  const body = payload.slice(0, idx + 4);
  return crc16ccitt(body) === payload.slice(idx + 4, idx + 8).toUpperCase();
}

/* ------------------------------------------------------------------ */
/* QR ตรวจสอบสลิป (slip verification QR) — รูปแบบที่สลิปจริงทุกใบใช้      */
/* ------------------------------------------------------------------ */

/** หัวของฟิลด์ CRC ในรูปแบบนี้: tag 91 ยาว 4 (EMVCo payment QR ใช้ tag 63 คนละตัวกัน) */
const SLIP_VERIFY_CRC_HEAD = "9104";

/**
 * ตรวจ CRC ที่ tag 91 — CRC-16/CCITT-FALSE ของทุกอักขระจนถึงและรวม `"9104"`
 * (ยืนยันกับสลิปจริง 34/34 ใบ ดู FORMATS.md หัวข้อ 1)
 *
 * - `true`  CRC ตรง เชื่อเนื้อ payload ได้ว่าอ่าน QR มาครบดวง
 * - `false` CRC ไม่ตรง **ห้ามคืน trans_ref จาก payload นี้**
 * - `null`  ไม่มี tag 91 ปิดท้าย = ไม่ใช่รูปแบบนี้ ไม่ใช่ "ผิด" (เช่น PromptPay payment QR)
 *
 * CRC ตรงไม่ได้แปลว่าตรวจสอบกับธนาคารแล้ว (กฎเหล็กข้อ 4) เป็นแค่หลักฐานว่าสตริงครบถ้วน
 */
export function checkSlipVerifyCrc(payload: string): boolean | null {
  const s = payload.replace(/\s+$/, "");
  const idx = s.lastIndexOf(SLIP_VERIFY_CRC_HEAD);
  if (idx < 0 || idx + 8 !== s.length) return null;
  // `toUpperCase()` ตรงนี้แตะเฉพาะเลขฐานสิบหก 4 ตัวของ CRC เท่านั้น ไม่ได้แตะ trans_ref
  return crc16ccitt(s.slice(0, idx + 4)) === s.slice(idx + 4).toUpperCase();
}

/**
 * อ่านชั้น A จากรูปแบบ QR ตรวจสอบสลิป: `00.01` = รหัสธนาคาร · `00.02` = เลขอ้างอิง
 * คืน `null` เมื่อ payload ไม่มีโหนดสองตัวนี้ = ไม่ใช่รูปแบบนี้
 *
 * `ref` เป็นค่าที่ยกมาจากโหนดตรงตัว ไม่แปลงอะไรเลยแม้แต่ตัวเดียว (กฎเหล็กข้อ 6)
 */
function pickFromSlipVerify(
  flat: Array<TlvNode & { path: string }>,
): { bank_code: string | null; ref: string; rule: string } | null {
  const refNode = flat.find((n) => n.path === "00.02");
  if (!refNode || refNode.value.length === 0) return null;
  const bankNode = flat.find((n) => n.path === "00.01");
  // รับรหัส 3 หลักตามที่ QR บอกมาตรงๆ ไม่กรองด้วยตาราง ธปท. เพราะธนาคารใหม่ที่ยังไม่อยู่ในตาราง
  // ก็ยังต้องได้ bank_code จริงของมัน (ตารางใช้แค่แปลงเป็นชื่อย่อตอนทำรายงาน)
  const bank_code = bankNode && /^[0-9]{3}$/.test(bankNode.value) ? bankNode.value : null;
  return { bank_code, ref: refNode.value, rule: `tlv/slip-verify@${refNode.path}` };
}

function pickBankCodeFromTlv(
  flat: Array<TlvNode & { path: string }>,
): { code: string; rule: string } | null {
  // 1. โหนดที่เป็นเลข 3 หลักและอยู่ในตารางรหัสธนาคาร ธปท. — สัญญาณตรงที่สุด
  for (const n of flat) {
    if (n.children) continue;
    if (/^[0-9]{3}$/.test(n.value) && isBotBankCode(n.value)) {
      return { code: n.value, rule: `tlv/bank-code@${n.path}` };
    }
  }
  // 2. โหนดยาวที่ขึ้นต้นด้วยรหัสธนาคาร (บางธนาคารต่อรหัสไว้หน้าเลขอ้างอิง)
  //    heuristic — ไม่แตะ trans_ref แค่ "อ่าน" prefix เท่านั้น ดู FORMATS.md หัวข้อ "ข้อจำกัด"
  for (const n of flat) {
    if (n.children) continue;
    if (n.value.length >= 12 && /^[0-9]{3}/.test(n.value) && isBotBankCode(n.value.slice(0, 3))) {
      return { code: n.value.slice(0, 3), rule: `tlv/bank-prefix@${n.path}` };
    }
  }
  return null;
}

function pickTransRefFromTlv(
  flat: Array<TlvNode & { path: string }>,
): { ref: string; rule: string } | null {
  const candidates = flat
    .filter((n) => !n.children)
    .filter((n) => !DENY_TAGS.has(n.tag))
    .filter((n) => !DENY_VALUES.has(n.value))
    .filter((n) => !Object.prototype.hasOwnProperty.call(KNOWN_AIDS, n.value))
    .filter((n) => !(/^[0-9]{3}$/.test(n.value) && isBotBankCode(n.value)))
    .filter((n) => looksLikeRef(n.value));

  if (candidates.length === 0) return null;
  // ยาวสุดชนะ เท่ากันเอาตัวที่อยู่ต้น payload ก่อน (เสถียร ไม่ขึ้นกับลำดับ filter)
  candidates.sort((a, b) => b.value.length - a.value.length || a.offset - b.offset);
  const top = candidates[0]!;
  return { ref: top.value, rule: `tlv/ref@${top.path}` };
}

/* ------------------------------------------------------------------ */
/* URL                                                                 */
/* ------------------------------------------------------------------ */

/** ชื่อ query param ที่พบว่าใช้เก็บเลขอ้างอิงบนลิงก์ตรวจสลิป เรียงตามลำดับความน่าเชื่อถือ */
const URL_REF_KEYS = [
  "transref",
  "trans_ref",
  "transactionref",
  "transactionid",
  "transaction_id",
  "refnbr",
  "refno",
  "ref_no",
  "reference",
  "ref",
  "txnref",
  "data",
];

const URL_BANK_KEYS = ["sendingbank", "sending_bank", "bank", "bankcode", "bank_code"];

function parseUrlPayload(raw: string): PayloadParseResult | null {
  let u: URL;
  try {
    u = new URL(raw.trim());
  } catch {
    return null;
  }
  if (u.protocol !== "http:" && u.protocol !== "https:") return null;

  const params = new Map<string, string>();
  u.searchParams.forEach((v, k) => {
    if (!params.has(k.toLowerCase())) params.set(k.toLowerCase(), v);
  });

  let trans_ref: string | null = null;
  let rule: string | null = null;
  for (const key of URL_REF_KEYS) {
    const v = params.get(key);
    if (v && looksLikeRef(v)) {
      trans_ref = v; // ตรงตัวจาก query string ไม่ decode ซ้ำ ไม่ upper
      rule = `url/param:${key}`;
      break;
    }
  }

  // บางลิงก์ฝังเลขอ้างอิงไว้ท้าย path แทน query
  if (!trans_ref) {
    const seg = u.pathname.split("/").filter(Boolean).pop();
    if (seg && looksLikeRef(seg)) {
      trans_ref = seg;
      rule = "url/last-path-segment";
    }
  }

  let bank_code: string | null = null;
  for (const key of URL_BANK_KEYS) {
    const v = params.get(key);
    if (v && /^[0-9]{3}$/.test(v) && isBotBankCode(v)) {
      bank_code = v;
      break;
    }
  }
  if (!bank_code) {
    const mapped = HOST_TO_BANK[u.hostname.toLowerCase()];
    if (mapped) bank_code = mapped;
  }

  return { format: "url", bank_code, trans_ref, rule };
}

/* ------------------------------------------------------------------ */
/* จุดเข้าใช้งาน                                                        */
/* ------------------------------------------------------------------ */

/**
 * แยก payload หนึ่งสตริงเป็นข้อมูลชั้น A
 * ไม่โยน exception — payload ที่อ่านไม่ออกคืน format `unknown` พร้อม trans_ref = null
 */
export function parsePayload(raw: string): PayloadParseResult {
  if (typeof raw !== "string" || raw.length === 0) {
    return { format: "unknown", bank_code: null, trans_ref: null, rule: null };
  }

  if (/^https?:\/\//i.test(raw.trim())) {
    const viaUrl = parseUrlPayload(raw);
    if (viaUrl) return viaUrl;
  }

  const nodes = parseTlvDeep(raw);
  if (nodes) {
    const flat = flattenTlv(nodes);

    // 1. รูปแบบ QR ตรวจสอบสลิป — ลองก่อนเสมอ เพราะสลิปจริงทุกใบที่เก็บมาใช้รูปแบบนี้
    //    ส่วนกฎเดา (PromptPay payment QR) ข้างล่างยังไม่เคยเจอของจริงสักใบ (FORMATS.md หัวข้อ 3)
    const slip = pickFromSlipVerify(flat);
    if (slip) {
      const crcOk = checkSlipVerifyCrc(raw);
      if (crcOk === false) {
        // อ่าน QR มาไม่ครบดวงหรือเพี้ยน — ทั้ง payload เชื่อไม่ได้ ไม่ใช่แค่ตัว CRC
        // นี่คือ "อ่านไม่ได้" ไม่ใช่การปฏิเสธสลิป (กฎเหล็กข้อ 11) ใบนี้ไปเส้นทาง AI ตาม SOT §7
        return {
          format: "emvco-tlv",
          bank_code: null,
          trans_ref: null,
          rule: null,
          reason: "crc-mismatch",
        };
      }
      if (crcOk === true) {
        return {
          format: "emvco-tlv",
          bank_code: slip.bank_code,
          trans_ref: slip.ref,
          rule: slip.rule,
        };
      }
      // crcOk === null → ไม่มี tag 91 ปิดท้าย ยังไม่ใช่รูปแบบนี้เต็มรูป ตกไปทางสำรอง
    }

    // 2. ทางสำรอง — กฎทั่วไป/PromptPay payment QR
    const bank = pickBankCodeFromTlv(flat);
    const ref = pickTransRefFromTlv(flat);
    return {
      format: "emvco-tlv",
      bank_code: bank?.code ?? null,
      trans_ref: ref?.ref ?? null,
      rule: ref?.rule ?? bank?.rule ?? null,
    };
  }

  return { format: "unknown", bank_code: null, trans_ref: null, rule: null };
}
